// sections/SocialProofSection.tsx
import React, { useState } from "react";
import { Container, Row, Col, Card, CardBody, Button } from "reactstrap";
import { Star, ChevronUp, ChevronDown } from "lucide-react";
import Slider from "react-slick";
import type { Settings } from "react-slick";
import "slick-carousel/slick/slick.css";
import "slick-carousel/slick/slick-theme.css";

// Import testimonial images
import testimonial1 from "./images/testimonial-1.avif";
import testimonial2 from "./images/testimonial-2.avif";
import testimonial3 from "./images/testimonial-3.avif";
import testimonial4 from "./images/testimonial-4.avif";
import testimonial5 from "./images/testimonial-5.avif";

interface Testimonial {
  name: string;
  project: string;
  image: string;
  quote: string;
}

const testimonials: Testimonial[] = [
  {
    name: "Sarah M.",
    project: "Kitchen Remodel",
    image: testimonial1,
    quote:
      "Found the perfect contractor for our kitchen remodel. The whole process was smooth and transparent.",
  },
  {
    name: "John D.",
    project: "Bathroom Renovation",
    image: testimonial2,
    quote:
      "Great platform! Got multiple competitive bids and completed our project under budget.",
  },
  {
    name: "Emily R.",
    project: "Deck Construction",
    image: testimonial3,
    quote:
      "The contractor verification process gave us peace of mind. Excellent service!",
  },
  {
    name: "Michael P.",
    project: "Home Addition",
    image: testimonial4,
    quote:
      "BidBro made it easy to find qualified contractors. Our home addition turned out beautifully.",
  },
  {
    name: "Lisa K.",
    project: "Landscaping Project",
    image: testimonial5,
    quote:
      "The competitive bidding process helped us get the best value for our landscaping project.",
  },
];

const TestimonialCard: React.FC<{ testimonial: Testimonial }> = ({ testimonial }) => {
  const [isExpanded, setIsExpanded] = useState(false);

  return (
    <Card className="h-100 shadow-sm hover-shadow testimonial-card">
      <CardBody className="p-4">
        <div className="d-flex align-items-center mb-4">
          <img
            src={testimonial.image}
            alt={testimonial.name}
            className="testimonial-avatar rounded-circle me-3"
          />
          <div>
            <div className="fw-semibold">{testimonial.name}</div>
            <div className="small text-muted">{testimonial.project}</div>
          </div>
        </div>
        <div className="flex-grow-1">
          <p
            className={`text-muted mb-2 testimonial-quote ${!isExpanded ? "collapsed" : ""}`}
          >
            {testimonial.quote}
          </p>
          <Button
            color="link"
            onClick={() => setIsExpanded(!isExpanded)}
            className="px-0 py-1 mb-2 shadow-none text-primary text-decoration-none d-flex align-items-center"
          >
            {isExpanded ? (
              <>
                Show less <ChevronUp className="ms-1" size={16} />
              </>
            ) : (
              <>
                Show more <ChevronDown className="ms-1" size={16} />
              </>
            )}
          </Button>
        </div>
        <div className="text-warning">
          {[...Array(5)].map((_, i) => (
            <Star key={i} className="rating-star fill-current" />
          ))}
        </div>
      </CardBody>
    </Card>
  );
};

const SocialProofSection: React.FC = () => {
  const stats = [
    { value: "150K+", label: "Verified Contractors" },
    { value: "4.8/5", label: "Average Rating" },
    { value: "95%", label: "Satisfaction Rate" },
  ];

  const sliderSettings: Settings = {
    dots: true,
    infinite: true,
    speed: 500,
    slidesToShow: 3,
    slidesToScroll: 1,
    autoplay: true,
    autoplaySpeed: 5000,
    pauseOnHover: true,
    responsive: [
      {
        breakpoint: 1024,
        settings: {
          slidesToShow: 2,
          slidesToScroll: 1,
        },
      },
      {
        breakpoint: 768,
        settings: {
          slidesToShow: 1,
          slidesToScroll: 1,
        },
      },
    ],
  };

  return (
    <section className="py-5 bg-light">
      <Container>
        <h2 className="fs-1 fw-bold text-center mb-3">
          Trusted by Homeowners
        </h2>
        <p className="text-center text-muted mb-5">
          Join thousands of satisfied customers who found their perfect
          contractor
        </p>

        <Row className="mb-5">
          {stats.map((stat, index) => (
            <Col md="4" className="text-center" key={index}>
              <div className="display-5 fw-bold text-primary mb-2">{stat.value}</div>
              <div className="text-muted">{stat.label}</div>
            </Col>
          ))}
        </Row>

        <div className="testimonial-slider">
            {/* @ts-ignore */}
          <Slider {...sliderSettings}>
            {testimonials.map((testimonial, index) => (
              <div key={index} className="p-2">
                <TestimonialCard testimonial={testimonial} />
              </div>
            ))}
          </Slider>
        </div>
      </Container>
    </section>
  );
};

export default SocialProofSection;